import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { COACH } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MapPin, PlayCircle, ShieldCheck, Trophy, Star, TrendingUp, Quote } from "lucide-react";

export default function CoachProfile() {
    const { id } = useParams();
    const [coach, setCoach] = useState(null);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        goal: "autre",
        message: "",
    });
    const [sending, setSending] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/coaches/${id}`);
            setCoach(data);
        })();
    }, [id]);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post(`/coaches/${id}/discovery`, form);
            toast.success("Demande envoyée !", {
                description: "Le coach vous recontactera sous 24h.",
            });
            setOpen(false);
            setForm({ name: "", email: "", phone: "", goal: "autre", message: "" });
        } catch {
            toast.error("Impossible d'envoyer la demande. Réessaie.");
        } finally {
            setSending(false);
        }
    };

    if (!coach)
        return (
            <div className="mx-auto max-w-[1400px] p-12">
                <div className="h-96 animate-pulse border border-white/10 bg-[#121212]" />
            </div>
        );

    return (
        <div className="mx-auto max-w-[1400px] px-6 py-10">
            <Link
                to="/"
                className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 hover:text-white"
            >
                ← Retour à l&apos;annuaire
            </Link>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
                {/* Hero / Video */}
                <div className="col-span-full lg:col-span-8">
                    <div className="relative aspect-video overflow-hidden border border-white/10 bg-[#121212]">
                        <img
                            src={coach.photo_url}
                            alt={coach.name}
                            className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/80 via-transparent to-transparent">
                            <PlayCircle className="h-16 w-16 text-white/80" />
                        </div>
                        <div className="absolute bottom-0 left-0 w-full border-t border-white/10 bg-black/60 p-4 backdrop-blur-sm">
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                                Présentation 1 minute
                            </span>
                        </div>
                    </div>

                    <div className="mt-6 border border-white/10 p-6">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                            <div>
                                <span
                                    className={`font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1 ${
                                        coach.category === "physical"
                                            ? "bg-[#FF2E2E] text-white"
                                            : "border border-white/40 text-white"
                                    }`}
                                >
                                    {coach.category === "physical" ? "Corps" : "Esprit"}
                                </span>
                                <h1 className="mt-3 font-display text-5xl uppercase leading-none tracking-tight text-white sm:text-6xl">
                                    {coach.name}
                                </h1>
                                <div className="mt-3 flex items-center gap-4 font-mono text-xs uppercase tracking-widest text-neutral-400">
                                    <span className="inline-flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {coach.city} {coach.remote && "· À distance"}
                                    </span>
                                    <span>·</span>
                                    <span>{coach.age} ans</span>
                                    <span>·</span>
                                    <span className="text-white">€{coach.hourly_rate}/h</span>
                                </div>
                            </div>
                            <Dialog open={open} onOpenChange={setOpen}>
                                <DialogTrigger asChild>
                                    <Button
                                        data-testid={COACH.bookBtn}
                                        className="h-12 rounded-none bg-[#FF2E2E] px-6 font-display text-xl uppercase tracking-tight text-white hover:bg-[#D61F1F]"
                                    >
                                        Réserver une séance découverte
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="rounded-none border border-white/10 bg-[#0A0A0A] text-white sm:max-w-md">
                                    <DialogHeader>
                                        <DialogTitle className="font-display text-2xl uppercase tracking-tight">
                                            Découverte avec {coach.name}
                                        </DialogTitle>
                                    </DialogHeader>
                                    <form
                                        data-testid={COACH.bookForm}
                                        onSubmit={submit}
                                        className="space-y-4"
                                    >
                                        <Input
                                            data-testid={COACH.bookName}
                                            placeholder="Nom complet"
                                            required
                                            value={form.name}
                                            onChange={(e) =>
                                                setForm({ ...form, name: e.target.value })
                                            }
                                            className="rounded-none border-white/10 bg-[#121212]"
                                        />
                                        <Input
                                            data-testid={COACH.bookEmail}
                                            type="email"
                                            placeholder="E-mail"
                                            required
                                            value={form.email}
                                            onChange={(e) =>
                                                setForm({ ...form, email: e.target.value })
                                            }
                                            className="rounded-none border-white/10 bg-[#121212]"
                                        />
                                        <Input
                                            data-testid="coach-book-phone"
                                            type="tel"
                                            placeholder="Téléphone"
                                            value={form.phone}
                                            onChange={(e) =>
                                                setForm({ ...form, phone: e.target.value })
                                            }
                                            className="rounded-none border-white/10 bg-[#121212]"
                                        />
                                        <select
                                            data-testid="coach-book-goal"
                                            value={form.goal}
                                            onChange={(e) =>
                                                setForm({ ...form, goal: e.target.value })
                                            }
                                            className="h-10 w-full rounded-none border border-white/10 bg-[#121212] px-3 text-sm text-white focus:border-[#FF2E2E] focus:outline-none"
                                        >
                                            <option value="musculation">Musculation</option>
                                            <option value="perte_poids">Perte de poids</option>
                                            <option value="mental_burnout">Mental / Burnout</option>
                                            <option value="autre">Autre</option>
                                        </select>
                                        <Textarea
                                            data-testid={COACH.bookMessage}
                                            placeholder="Message — ton contexte, ton calendrier"
                                            required
                                            rows={4}
                                            value={form.message}
                                            onChange={(e) =>
                                                setForm({ ...form, message: e.target.value })
                                            }
                                            className="rounded-none border-white/10 bg-[#121212]"
                                        />
                                        <Button
                                            data-testid={COACH.bookSubmit}
                                            disabled={sending}
                                            className="h-11 w-full rounded-none bg-[#FF2E2E] font-display text-lg uppercase tracking-tight hover:bg-[#D61F1F]"
                                        >
                                            {sending ? "Envoi..." : "Envoyer la demande"}
                                        </Button>
                                    </form>
                                </DialogContent>
                            </Dialog>
                        </div>

                        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-neutral-300">
                            {coach.bio}
                        </p>

                        {coach.testimonial?.quote && (
                            <figure
                                data-testid="coach-testimonial"
                                className="mt-6 grid grid-cols-1 gap-4 border-l-2 border-[#FF2E2E] bg-[#0a0a0a] p-5 md:grid-cols-5"
                            >
                                <div className={coach.testimonial.video_data_url ? "md:col-span-3" : "md:col-span-5"}>
                                    <Quote className="h-5 w-5 text-[#FF2E2E]" />
                                    <blockquote className="mt-3 font-display text-xl uppercase leading-tight text-white sm:text-2xl">
                                        « {coach.testimonial.quote} »
                                    </blockquote>
                                    <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                        — {coach.testimonial.student}
                                        {coach.testimonial.duration ? `, ${coach.testimonial.duration}` : ""} · Élève vérifié·e
                                    </figcaption>
                                </div>
                                {coach.testimonial.video_data_url && (
                                    <div className="md:col-span-2">
                                        <video
                                            data-testid="coach-testimonial-video"
                                            src={coach.testimonial.video_data_url}
                                            controls
                                            playsInline
                                            className="aspect-video w-full border border-white/10 bg-black object-cover"
                                        />
                                        <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-500">
                                            15 s · Avis vidéo vérifié
                                        </div>
                                    </div>
                                )}
                            </figure>
                        )}

                        <div className="mt-6 flex flex-wrap gap-2">
                            {(coach.specialties || []).map((s) => (
                                <span
                                    key={s}
                                    className="border border-white/10 px-3 py-1 text-xs uppercase tracking-widest text-white"
                                >
                                    {s}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Certifications */}
                    <div className="mt-6 border border-white/10 p-6">
                        <h2 className="font-display text-2xl uppercase tracking-tight text-white">
                            Diplômes
                        </h2>
                        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                            {(coach.certifications || []).map((c, i) => (
                                <div
                                    key={i}
                                    className="flex items-center justify-between border border-white/10 bg-[#121212] p-4"
                                >
                                    <div>
                                        <div className="font-display text-lg uppercase text-white">
                                            {c.name}
                                        </div>
                                        <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                            {c.issuer}
                                        </div>
                                    </div>
                                    {c.verified ? (
                                        <span className="inline-flex items-center gap-1 border border-[#FF2E2E] px-2 py-1 text-[10px] uppercase tracking-widest text-[#FF2E2E]">
                                            <ShieldCheck className="h-3 w-3" /> Vérifié
                                        </span>
                                    ) : (
                                        <span className="text-[10px] uppercase tracking-widest text-neutral-600">
                                            En attente
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Merit panel */}
                <aside className="col-span-full lg:col-span-4">
                    <div className="sticky top-20 space-y-4">
                        <div className="border border-white/10 bg-[#121212] p-6">
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                                    Score de mérite
                                </span>
                                <span className="h-1.5 w-1.5 bg-[#FF2E2E]" />
                            </div>
                            <div
                                data-testid={COACH.meritScore}
                                className="mt-2 font-display text-7xl uppercase leading-none tracking-tight text-[#FF2E2E]"
                            >
                                {Math.round(coach.merit_score || 0)}
                            </div>
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Mérité, pas acheté.
                            </div>
                        </div>

                        <div
                            data-testid={COACH.goals}
                            className="flex items-center justify-between border border-white/10 bg-[#121212] p-5"
                        >
                            <div className="flex items-center gap-3">
                                <Trophy className="h-5 w-5 text-[#FF2E2E]" />
                                <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">
                                    Objectifs Élèves Atteints
                                </span>
                            </div>
                            <span className="font-display text-3xl text-white">
                                {coach.goals_achieved}
                            </span>
                        </div>

                        <div
                            data-testid={COACH.rating}
                            className="border border-white/10 bg-[#121212] p-5"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Star className="h-5 w-5 text-[#FF2E2E]" />
                                    <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">
                                        Avis Vérifiés
                                    </span>
                                </div>
                                <span className="font-display text-3xl text-white">
                                    {coach.verified_rating ? `${coach.verified_rating.toFixed(1)}/5` : "—"}
                                </span>
                            </div>
                            <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                Issus de {coach.verified_rating_count} élèves (3+ séances)
                            </div>
                        </div>

                        <div
                            data-testid={COACH.retention}
                            className="flex items-center justify-between border border-white/10 bg-[#121212] p-5"
                        >
                            <div className="flex items-center gap-3">
                                <TrendingUp className="h-5 w-5 text-[#FF2E2E]" />
                                <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">
                                    Taux de Rétention
                                </span>
                            </div>
                            <span className="font-display text-3xl text-white">
                                {Math.round((coach.retention_rate || 0) * 100)}%
                            </span>
                        </div>

                        <div className="border border-dashed border-white/10 p-4 text-[10px] uppercase tracking-[0.3em] text-neutral-600">
                            // Zéro followers · Zéro endossement · Zéro post sponsorisé
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}
